/**
 * G2eGente - Resposta Pública NR01 (Sem Cadastro)
 * Permite que qualquer pessoa responda ao diagnóstico via link
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { 
  ShieldCheck,
  Building2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Lock,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SCALE_LABELS = ['Nunca', 'Raramente', 'Às vezes', 'Frequentemente', 'Sempre'];

export default function PublicNR01Response() {
  const { codigo } = useParams();
  const navigate = useNavigate();
  
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedSetor, setSelectedSetor] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState('setor'); // setor, questions, done

  useEffect(() => {
    fetchDiagnostic();
  }, [codigo]);

  const fetchDiagnostic = async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/nr01/${codigo}`);
      setDiagnostic(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Diagnóstico não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (value) => {
    const pergunta = diagnostic.perguntas[currentQuestion];
    setAnswers(prev => ({
      ...prev,
      [pergunta.id]: parseInt(value)
    }));
  };

  const handleNext = () => {
    if (currentQuestion < diagnostic.perguntas.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // Verificar se todas as perguntas foram respondidas
    const unanswered = diagnostic.perguntas.filter(p => !answers[p.id]);
    if (unanswered.length > 0) {
      toast.error(`Responda todas as perguntas. Faltam ${unanswered.length}.`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        respostas: Object.entries(answers).map(([pergunta_id, valor]) => ({
          pergunta_id,
          valor
        })),
        setor: selectedSetor
      };

      await axios.post(`${API}/api/public/nr01/${codigo}/responder`, payload);
      setSubmitted(true);
      setStep('done');
      toast.success('Resposta enviada com sucesso!');
    } catch (err) {
      toast.error('Erro ao enviar resposta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Diagnóstico Indisponível</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Select Sector
  if (step === 'setor') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 p-4">
        <div className="max-w-2xl mx-auto pt-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">{diagnostic.titulo}</h1>
            <div className="flex items-center justify-center gap-2 text-slate-600">
              <Building2 className="w-4 h-4" />
              <span>{diagnostic.empresa}</span>
            </div>
          </div>

          {/* Privacy Notice */}
          <Card className="mb-6 bg-emerald-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-emerald-800 mb-1">100% Anônimo</h4>
                  <p className="text-sm text-emerald-700">
                    Suas respostas são completamente anônimas. Não coletamos nenhuma informação 
                    que possa identificar você. Responda com total sinceridade.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sector Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Selecione seu setor de trabalho</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                Escolha o ambiente onde você trabalha para uma análise mais precisa dos riscos.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {diagnostic.setores.map(setor => (
                  <button
                    key={setor}
                    onClick={() => setSelectedSetor(setor)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      selectedSetor === setor
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-slate-200 hover:border-amber-300'
                    }`}
                  >
                    <Building2 className={`w-5 h-5 mb-2 ${selectedSetor === setor ? 'text-amber-600' : 'text-slate-400'}`} />
                    <span className={selectedSetor === setor ? 'text-amber-800 font-medium' : 'text-slate-700'}>
                      {setor}
                    </span>
                  </button>
                ))}
              </div>

              <Button
                onClick={() => setStep('questions')}
                disabled={!selectedSetor}
                className="w-full mt-6 bg-amber-600 hover:bg-amber-700"
                data-testid="start-diagnostic-btn"
              >
                Iniciar Diagnóstico
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Step: Done
  if (step === 'done') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Obrigado!</h2>
            <p className="text-slate-600 mb-6">
              Sua resposta foi enviada com sucesso. Sua participação é fundamental para 
              melhorar o ambiente de trabalho.
            </p>
            <p className="text-sm text-slate-500">
              Você pode fechar esta página agora.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Questions
  const pergunta = diagnostic.perguntas[currentQuestion];
  const dimensao = diagnostic.dimensoes?.find(d => d.id === pergunta.dimensao);
  const progress = ((currentQuestion + 1) / diagnostic.perguntas.length) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 p-4">
      <div className="max-w-2xl mx-auto pt-4">
        {/* Progress Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
            <span>Pergunta {currentQuestion + 1} de {diagnostic.perguntas.length}</span>
            <span>{answeredCount} respondidas</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="mb-4">
          <CardContent className="p-6">
            {dimensao && (
              <div className="text-xs font-medium text-amber-600 uppercase tracking-wide mb-3">
                {dimensao.nome}
              </div>
            )}
            
            <h3 className="text-lg font-semibold text-slate-800 mb-6">
              {pergunta.texto}
            </h3>

            <RadioGroup
              value={answers[pergunta.id]?.toString() || ''}
              onValueChange={handleAnswer}
              className="space-y-3"
            >
              {[1, 2, 3, 4, 5].map(value => (
                <label
                  key={value}
                  className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    answers[pergunta.id] === value
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-slate-200 hover:border-amber-300'
                  }`}
                >
                  <RadioGroupItem value={value.toString()} />
                  <div className="flex-1">
                    <span className={`font-medium ${answers[pergunta.id] === value ? 'text-amber-800' : 'text-slate-700'}`}>
                      {value}. {SCALE_LABELS[value - 1]}
                    </span>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentQuestion === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Anterior
          </Button>

          {currentQuestion < diagnostic.perguntas.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!answers[pergunta.id]}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Próxima
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || answeredCount < diagnostic.perguntas.length}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-diagnostic-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Enviar Respostas
            </Button>
          )}
        </div>

        {/* Quick Jump */}
        <div className="mt-6">
          <div className="flex flex-wrap gap-1 justify-center">
            {diagnostic.perguntas.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentQuestion(idx)}
                className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
                  idx === currentQuestion
                    ? 'bg-amber-600 text-white'
                    : answers[diagnostic.perguntas[idx].id]
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
